/** Reads follow-up chip options from AI SDK UI message `parts` (stream type `data-suggestions`). */

import { hasSupportHandoff } from './data-support-handoff';
import { messageHasToolProgress } from './tool-progress';

export type UIMessagePartsLike = {
  parts?: Array<{ type?: string; data?: unknown; [key: string]: unknown }>;
};

/** Minimal shape for scanning a thread for the latest follow-up chips in the active turn. */
export type UIMessageRoleLike = UIMessagePartsLike & {
  role?: string;
};

/** One follow-up chip from the agent stream; `use_case` is set when the server emits structured routing. */
export type DataSuggestionOption = { text: string; use_case?: string };

function _parseOption(raw: unknown): DataSuggestionOption | null {
  if (typeof raw === 'string') {
    const text = raw.trim();
    return text.length > 0 ? { text } : null;
  }
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const o = raw as { text?: unknown; use_case?: unknown };
  if (typeof o.text !== 'string') {
    return null;
  }
  const text = o.text.trim();
  if (!text.length) {
    return null;
  }
  const use_case =
    typeof o.use_case === 'string' && o.use_case.trim().length > 0 ? o.use_case.trim() : undefined;
  return use_case ? { text, use_case } : { text };
}

/**
 * Returns the latest non-empty ``data-suggestions`` options from message parts (nearest to the end wins).
 * Supports legacy string options and structured ``{ text, use_case }`` objects from the agent.
 */
export function getDataSuggestionsOptions(message: UIMessagePartsLike): DataSuggestionOption[] | undefined {
  const parts = message.parts;
  if (!parts?.length) {
    return undefined;
  }
  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i] as { type?: string; data?: { options?: unknown } };
    if (p.type !== 'data-suggestions' || !p.data || !Array.isArray(p.data.options)) {
      continue;
    }
    const out: DataSuggestionOption[] = [];
    for (const item of p.data.options) {
      const parsed = _parseOption(item);
      if (parsed) {
        out.push(parsed);
      }
    }
    if (out.length) {
      return out;
    }
  }
  return undefined;
}

/**
 * Returns follow-up chips from the latest assistant message after the last user message.
 * Use on the visible last assistant row when chips landed on a filtered trailing shell in the same turn.
 */
export function getFollowUpsAfterLastUserMessage(
  messages: readonly UIMessageRoleLike[]
): DataSuggestionOption[] | undefined {
  let lastUserIndex = -1;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user') {
      lastUserIndex = i;
      break;
    }
  }
  for (let i = messages.length - 1; i > lastUserIndex; i -= 1) {
    if (messages[i]?.role === 'assistant') {
      const opts = getDataSuggestionsOptions(messages[i]);
      if (opts?.length) {
        return opts;
      }
    }
  }
  return undefined;
}

/** Follow-up chips are hidden when a support handoff is present — the handoff card is the only CTA. */
export function shouldShowAdvisorFollowUpChips(
  message: UIMessagePartsLike,
  followOpts: DataSuggestionOption[] | undefined,
  { isLast, busy }: { isLast: boolean; busy: boolean }
): boolean {
  return Boolean(isLast && !busy && followOpts?.length && !hasSupportHandoff(message));
}

/**
 * Assistant shell rows with only progress, suggestion, or handoff parts must not be hidden as "empty".
 *
 * ``busy`` distinguishes cases that look identical from the parts alone. A finished row carrying
 * only a card is real content and stays. A row mid-turn is not: the handoff part is emitted when
 * the model calls ``route_to_support``, while its sentence comes from the support lane a round
 * trip later, so treating it as content renders the card alone - no words, and no thinking
 * indicator either, since the row is no longer a trailing empty assistant. Tool progress is the
 * exception: it is what the row shows while the turn runs.
 */
export function isAssistantMessageShellEmpty(
  message: UIMessagePartsLike,
  displayText: string,
  busy: boolean
): boolean {
  if (displayText.trim().length > 0) {
    return false;
  }
  if (busy) {
    return !messageHasToolProgress(message);
  }
  return !Boolean(getDataSuggestionsOptions(message)?.length || hasSupportHandoff(message));
}
